<template>
  <div class="border-top p-2 d-flex align-items-end rs-bg-content">
    <div class="mr-2" :style="{ width: '100px' }">
      <label>Link Type</label>
      <BFormSelect v-model="linkType" :options="linkTypes" size="sm" />
    </div>

    <div class="mr-2 flex-grow-1">
      <label>{{ linkTypes.find((l) => l.value === linkType)?.text }}</label>
      <BInputGroup size="sm">
        <BInputGroupPrepend v-if="!['Email', 'Phone'].includes(linkType)">
          <BButton title="Upload and link to file" @click="handleUploadAndLink">
            <i class="far fa-file-plus" />
          </BButton>
        </BInputGroupPrepend>

        <BFormInput
          :value="href"
          @input="handleChange('href', formatHref[linkType]($event))"
        />

        <BInputGroupAppend v-if="!['Email', 'Phone'].includes(linkType)">
          <BButton
            v-if="href"
            :href="href"
            target="_blank"
            variant="outline-secondary"
            title="Open link in new tab"
          >
            <i class="far fa-external-link" />
          </BButton>
        </BInputGroupAppend>
      </BInputGroup>
    </div>

    <div :style="{ width: '135px' }">
      <label>Target</label>
      <BFormSelect
        :value="target"
        size="sm"
        :options="targetTypes"
        @change="handleChange('target', $event)"
      />
    </div>

    <div class="flex-grow-0 align-self-end ml-2">
      <BButton
        size="sm"
        variant="light"
        title="Remove Link"
        @click="editor.chain().focus().unsetLink().run()"
      >
        <i class="far fa-trash-alt rs-text-error" />
      </BButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { BFormSelect, BInputGroupPrepend } from 'bootstrap-vue';
import { Editor } from '@tiptap/vue-2';
import { linkTypes, targetTypes } from '@/shared/tiptap/consts';
import { uploadWithFileDialog } from '@/shared/tiptap/extensions/file-handler';
import { TEditableLink } from '@/shared/tiptap/types';

/**
 * Various methods for formatting the `href` attribute of the anchor tag we are making.
 */
export const formatHref: Record<string, (href: string) => string> = {
  URL: (href: string) => {
    if (!href.startsWith('http')) {
      return `https://${href}`
        .replace('mailto:', '')
        .replace('tel:+1', '');
    }

    return href;
  },

  Email: (href: string) => `mailto:${href}`
    .replace('https://', '')
    .replace('http://', '')
    .replace('tel:+1', ''),

  Phone: (href: string) => {
    const phoneFormatted = href.replace(/\D/g, '');

    return phoneFormatted.startsWith('1') ? `tel:+${phoneFormatted}` : `tel:+1${phoneFormatted}`;
  },
};

/**
 * Returns the link type based on `href` format.
 * @param href
 */
export const getHrefType = (href: string) => {
  if (href.startsWith('tel:+')) {
    return 'Phone';
  }

  if (href.startsWith('mailto:')) {
    return 'Email';
  }

  return 'URL';
};

/**
 * Formats the `href` for editing by removing its modifiers.
 * @param href
 */
export const unformatHref = (href: string) => {
  if (href.startsWith('tel:+')) {
    return href.replace('tel:+', '');
  }

  if (href.startsWith('mailto:')) {
    return href.replace('mailto:', '');
  }

  return href;
};

export default defineComponent({
  props: {
    /**
     * Editor instance shared by toolbar and form parent components.
     */
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },

    /**
     * Callback method for when a file upload has started on the link editor.
     */
    onFileUploadStart: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback method for when a file upload has succeeded on the link editor.
     */
    onFileUploadSuccess: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback method for when a file upload has failed on the link editor.
     */
    onFileUploadError: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },
  },

  data() {
    return {
      formatHref,
      linkTypes,
      targetTypes,
      linkType: 'URL',
      href: '',
      target: '_blank',
    };
  },

  watch: {
    linkType(newVal: string) {
      this.handleChange('href', this.formatHref[newVal](this.href));
    },

    'editor.state.selection': {
      handler() {
        if (this.editor.isFocused && this.editor.isActive('link')) {
          this.setInitialState();
        }
      },
    },
  },

  mounted() {
    this.setInitialState();
  },

  methods: {
    unformatHref,

    /**
     * Sets initial state for link's attributes.
     */
    setInitialState() {
      const link = this.editor.getAttributes('link');
      this.href = unformatHref(link.href ?? '');
      this.linkType = getHrefType(link.href);
      this.target = link.target ?? '_blank';
    },

    /**
     * When attribute `key`'s `value` has changed, update it on the editor and emit it via `@input`
     * @param key
     * @param content
     */
    handleChange(key: keyof TEditableLink, content: string) {
      const link = this.editor.getAttributes('link') as TEditableLink;

      if (!link) {
        return;
      }

      const newValue = { ...link, [key]: content };

      this.editor
        .chain()
        .extendMarkRange('link')
        .setLink(newValue)
        .run();
    },

    /**
     * Handles a file upload for the link editor, then sets the link's `href` attribute
     * to the uploaded file's S3 location.
     */
    async handleUploadAndLink() {
      await uploadWithFileDialog({
        onUploadStart: this.onFileUploadStart,
        onUploadSuccess: (filename) => {
          this.href = filename;
          this.handleChange('href', filename);
          this.onFileUploadSuccess();
        },
        onUploadError: this.onFileUploadError,
        fileRestrict: 'none',
      });
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
